import type { AccountInfo, Connection } from '@solana/web3.js'
import type { Buffer } from 'node:buffer'
import { useWallet } from '@solana/wallet-adapter-vue'
import { PublicKey } from '@solana/web3.js'
import debounce from 'lodash-es/debounce'
import { defineStore } from 'pinia'
import { findAssociatedTokenAddress, lamportsToSol } from '~/utils'

const ACCOUNT_CHANGE_DEBOUNCE = 500

export const useBalanceStore = defineStore('balance', () => {
  const connectionStore = useConnectionStore()
  const stakePoolStore = useStakePoolStore()
  const { publicKey } = useWallet()
  const emitter = useEmitter()

  const nativeBalance = ref(0)
  const hasTokenAccount = ref<boolean>(false)
  const tokenBalanceLoading = ref<boolean>(false)
  const tokenBalance = ref(0)

  const stakePool = computed(() => stakePoolStore.stakePool)

  const solBalance = computed(() => lamportsToSol(nativeBalance.value))

  async function _getTokenBalance() {
    if (!publicKey.value || !stakePool.value?.poolMint) {
      return
    }
    tokenBalanceLoading.value = true
    const balance = await getTokenBalance(
      connectionStore.connection,
      publicKey.value,
      new PublicKey(stakePool.value?.poolMint),
    )
    hasTokenAccount.value = balance !== null
    tokenBalance.value = balance ?? 0
    tokenBalanceLoading.value = false
    console.log('[Balance] JSOL:', tokenBalance.value)
  }

  const _onAccountChange = debounce(async (acc: AccountInfo<Buffer> | null) => {
    nativeBalance.value = acc?.lamports ?? 0
    console.log('[Balance] SOL:', nativeBalance.value)
    await _getTokenBalance()
  }, ACCOUNT_CHANGE_DEBOUNCE)

  const _onDisconnect = () => {
    tokenBalanceLoading.value = false
    nativeBalance.value = 0
    tokenBalance.value = 0
  }

  emitter.on(ACCOUNT_CHANGE_EVENT, _onAccountChange)
  emitter.on(WALLET_DISCONNECT_EVENT, _onDisconnect)

  watch(
    publicKey,
    (pk) => {
      if (pk) {
        connectionStore.connection.getAccountInfo(pk).then(_onAccountChange)
        tokenBalanceLoading.value = true
      }
    },
    { immediate: true },
  )

  return {
    solBalance,
    nativeBalance,

    // tokens
    hasTokenAccount,
    tokenBalanceLoading,
    tokenBalance,
  }
})

/**
 * Gets TokenAccountBalance from the associated token account
 */
async function getTokenBalance(
  connection: Connection,
  walletAddress: PublicKey,
  mint: PublicKey,
): Promise<number | null> {
  try {
    const associatedTokenAcc = await findAssociatedTokenAddress(walletAddress, mint)
    const tBalance = await connection.getTokenAccountBalance(associatedTokenAcc)
    return tBalance.value.uiAmount
  } catch {
    // No token account found
    return null
  }
}
